import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { ROLES, Role } from '../../constants/roles';
import { useUnitats } from '../../hooks/useUnitats';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { extractErrorMessage } from '../../utils/errorHandling';
import { PageTitle, StandardContentContainer } from '../Shared/CommonStyles';
import {
	Button,
	ErrorMessage,
	FormContainer,
	FormGroup,
	Input,
	Label,
	Select,
} from '../Shared/FormStyles';
import { IUser } from './types';

interface UserFormProps {
	userToEdit: IUser | null;
	onSuccess: () => void;
	onCancel: () => void;
}

type UserFormData = {
	username: string;
	password?: string;
	roles: Role[];
	unitat?: string | null;
};

const UserForm: React.FC<UserFormProps> = ({
	userToEdit,
	onSuccess,
	onCancel,
}) => {
	const { unitats, isFetchingUnitats } = useUnitats();

	const isEditMode = !!userToEdit;

	const schema = yup.object().shape({
		username: yup
			.string()
			.required('Username is required')
			.min(2)
			.max(30),
		password: yup
			.string()
			.transform((value) => (value === '' ? undefined : value))
			.optional()
			.min(8, 'Password must be at least 8 characters'),
		roles: yup
			.array()
			.of(yup.string().oneOf(ROLES).required('Role cannot be empty'))
			.min(1, 'At least one role is required')
			.required('Roles are required')
			.defined(),
		unitat: yup
			.string()
			.nullable()
			.transform((value) => (value === '' ? null : value)),
	});

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UserFormData>({
		resolver: yupResolver(schema),
		defaultValues: {
			username: userToEdit?.username || '',
			password: '',
			roles: userToEdit?.roles || ['Familiar'],
			unitat: userToEdit?.unitat || null,
		},
	});

	useEffect(() => {
		if (userToEdit) {
			reset({
				username: userToEdit.username,
				password: '',
				roles: userToEdit.roles,
				unitat: userToEdit.unitat || null,
			});
		}
	}, [userToEdit, reset]);

	const mutation = useSimpleMutation<Partial<IUser>>({
		method: isEditMode ? 'put' : 'post',
		url: isEditMode ? `/api/users/${userToEdit?._id}` : '/api/users',
		callback: onSuccess,
		queriesToInvalidate: [['users']],
	});

	const onSubmit = (data: UserFormData) => {
		const sanitizedData: Partial<IUser> = {
			...data,
			unitat: data.unitat || undefined,
		};
		mutation.mutate(sanitizedData);
	};

	return (
		<StandardContentContainer>
			<PageTitle>{isEditMode ? 'Edit User' : 'Create User'}</PageTitle>
			<FormContainer onSubmit={handleSubmit(onSubmit)}>
				<FormGroup>
					<Label htmlFor="username">Username</Label>
					<Input id="username" {...register('username')} />
					{errors.username && (
						<ErrorMessage>{errors.username.message}</ErrorMessage>
					)}
				</FormGroup>

				<FormGroup>
					<Label htmlFor="password">
						Password {isEditMode ? '(leave blank to keep current)' : ''}
					</Label>
					<Input id="password" type="password" {...register('password')} />
					{errors.password && (
						<ErrorMessage>{errors.password.message}</ErrorMessage>
					)}
				</FormGroup>

				<FormGroup>
					<Label>Roles</Label>
					<div>
						{ROLES.map((role) => (
							<label key={role}>
								<input
									type="checkbox"
									value={role}
									{...register('roles')}
								/>
								{role}
							</label>
						))}
					</div>
					{errors.roles && (
						<ErrorMessage>{errors.roles.message}</ErrorMessage>
					)}
				</FormGroup>

				<FormGroup>
					<Label htmlFor="unitat">Unitat</Label>
					<Select id="unitat" {...register('unitat')} disabled={isFetchingUnitats}>
						<option value="">N/A</option>
						{unitats?.map((unitat) => (
							<option key={unitat._id} value={unitat._id}>
								{unitat.nom}
							</option>
						))}
					</Select>
					{errors.unitat && (
						<ErrorMessage>{errors.unitat.message}</ErrorMessage>
					)}
				</FormGroup>

				<Button type="submit" disabled={mutation.isPending}>
					{mutation.isPending
						? 'Saving...'
						: isEditMode
						? 'Update User'
						: 'Create User'}
				</Button>
				<Button
					type="button"
					onClick={onCancel}
					disabled={mutation.isPending}
					style={{ backgroundColor: '#6c757d' }}
				>
					Cancel
				</Button>

				{mutation.error && (
					<ErrorMessage>
						{extractErrorMessage(mutation.error)}
					</ErrorMessage>
				)}
			</FormContainer>
		</StandardContentContainer>
	);
};

export default UserForm;
